import React, {useState, useEffect, useCallback} from 'react';
import {Route, Routes, useLocation, useNavigate} from 'react-router-dom';
import Header from './components/Header/Header';
import MainContent from './components/MainContent'; // Make sure this component exists and is correctly imported
import Popup from './components/Popup';
import PopupAttorney from './components/PopupAttorney';
import PopupVendor from './components/Vendors/PopupVendor';
import {Helmet} from 'react-helmet';
import {SearchProvider} from './context/SearchContext';
import api from './api';
import FeedbackPopup from "./components/popups/FeedbackPopup";
import SendOkPopup from "./components/popups/SendOkPopup";
import SharePopup from "./components/popups/SendSharePopup";
import PageAbout from './components/pages/PageAbout';
import PageLawFirms from './components/pages/PageLawFirms';
import PageVendors from './components/pages/PageVendors';
import SignIn from "./components/User/SignIn";
import UserProfile from "./components/User/UserProfile";
import ForgotPassword from "./components/User/ForgotPassword";
import ResetPassword from './components/User/ResetPassword';
import SignUp from "./components/User/SignUp";
import {NotificationProvider} from "./context/NotificationContext";
import Notification from './components/Notification';
import ActivateAccount from "./components/User/ActivateAccount";
import ResendActivationEmail from "./components/User/ResendActivationEmail";
import PrivateRoute from "./components/ProtectedRoute"; // Note: Duplicate import below
import PrivacyPolicy from "./components/pages/PrivacyPolicy";
import TermsOfUse from "./components/pages/TermsOfUse";
import CookiePolicy from "./components/pages/CookiePolicy";
import DMCAPolicy from "./components/pages/DMCAPolicy";
import AttorneyAdvRules from "./components/pages/AttorneyAdvRules";
import SecurityPolicy from "./components/pages/SecurityPolicy";
import {GoogleOAuthProvider} from '@react-oauth/google';
import ProtectedRoute from "./components/ProtectedRoute"; // Note: Duplicate import above
import ActivateTokenAccount from "./components/User/ActivateTokenAccount";
import VerifyEmail from "./components/User/VerifyEmail";
import Accessibility from "./components/pages/Accessibility";
import VerifyPromoEmailToken from "./components/User/VerifyPromoEmailToken";
import VerifyNewPromoEmailToken from "./components/User/VerifyNewPromoEmailToken";
import DisplayPromoAttorneyInfo from "./components/User/ClaimConfirmationPage";
import SearchingRequestPage from "./components/SearchingRequestPage";
import ClaimVendorConfirmationPage from "./components/User/ClaimVendorConfirmationPage";
import MarketingClaimProfilePage from "./components/pages/MarketingClaimProfilePage";
import MarketingClaimVendorProfilePage from "./components/pages/MarketingClaimVendorProfilePage";


const App = ({region}) => {
    const [popupVisible, setPopupVisible] = useState(false);
    const [popupData, setPopupData] = useState(null);
    const [selectedAttorney, setSelectedAttorney] = useState(null);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const [historyStack, setHistoryStack] = useState([]);
    const [isIndexPage, setIsIndexPage] = useState(true); // State variable used for the new route
    const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
    const [showSendOkPopup, setShowSendOkPopup] = useState(false);
    const [showSharePopup, setShowSharePopup] = useState(false);
    const [currentURL, setCurrentURL] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [telephone, setTelephone] = useState('');
    const [address, setAddress] = useState('');
    const [specializations, setSpecializations] = useState([]);
    const [menuKey, setMenuKey] = useState(0);
    const [attorneyOpenedFromPopup, setAttorneyOpenedFromPopup] = useState(false);
    const [popupOrigin, setPopupOrigin] = useState(null);

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    // Redirect effect for '/' still active
    useEffect(() => {
        if (location.pathname === '/') {
            window.location.replace('https://ai.law.law');
        }
    }, [location.pathname]);

    // ** UPDATED this useEffect to include '/ai' **
    useEffect(() => {
        // Define which paths are considered "index" pages (affects header, layout, etc.)
        const indexPages = ['/', '/ai', '/bio', '/law-firms', '/vendors'];
        setIsIndexPage(indexPages.includes(location.pathname));
    }, [location.pathname]); // Dependency is correct

    // handleShowPopup - UNCHANGED
    const handleShowPopup = (id, type, origin = null) => {
        setPopupVisible(false);
        setPopupOrigin(origin);

        if (type === 'company') {
            // Navigate with query params, potentially relative to the current path (e.g., /ai?companyId=...)
            navigate(`?companyId=${id}`);
            api.get(`/legal-companies/${id}/`)
                .then(response => {
                    setPopupData(response.data);
                    setCompanyName(response.data.name);
                    setTelephone(response.data.phone_number);
                    setAddress(`${response.data.address.street_address}, ${response.data.address.city.name}, ${response.data.address.state.name}, ${response.data.address.country.name}, ${response.data.address.zip_code.zip_code}`);
                    setSpecializations(response.data.specializations.map(spec => spec.name));
                    setPopupVisible(true);
                    setSelectedAttorney(null);
                    setSelectedVendor(null);
                })
                .catch(error => console.error('Error fetching company data:', error));
        } else if (type === 'attorney') {
            navigate(`?attorneyId=${id}`);
            api.get(`/attorneys/${id}/`)
                .then(response => {
                    setSelectedAttorney(response.data);
                    setCompanyName(`${response.data.first_name} ${response.data.last_name}`);
                    setTelephone(response.data.phone_number);
                    setAddress(response.data.addresses.map(addr => `${addr.street_address}, ${addr.city.name}, ${addr.state.name}, ${addr.country.name}, ${addr.zip_code.zip_code}`).join('\n'));
                    setSpecializations(response.data.specializations.map(spec => spec.name));
                    setPopupVisible(true);
                    // setHistoryStack(prev => [...prev, location.pathname + location.search]); // History handling might need review
                    setCurrentURL(window.location.href);
                    setPopupData(null);
                    setSelectedVendor(null);
                })
                .catch(error => console.error('Error fetching attorney data:', error));
        } else if (type === 'vendor') {
            navigate(`?vendorId=${id}`);
            api.get(`/vendors/${id}/`)
                .then(response => {
                    setSelectedVendor(response.data);
                    setCompanyName(response.data.name);
                    setTelephone(response.data.phone_number);
                    setAddress(`${response.data.address.street_address}, ${response.data.address.city.name}, ${response.data.address.state.name}, ${response.data.address.country.name}, ${response.data.address.zip_code.zip_code}`);
                    setSpecializations(response.data.languages.map(lang => lang.name));
                    setPopupVisible(true);
                    // setHistoryStack(prev => [...prev, location.pathname + location.search]); // History handling might need review
                    setCurrentURL(window.location.href);
                    setPopupData(null);
                    setSelectedAttorney(null);
                })
                .catch(error => console.error('Error fetching vendor data:', error));
        }
    };

    // handleHidePopup - UNCHANGED (but check navigation logic)
    const handleHidePopup = () => {
        if (selectedAttorney && attorneyOpenedFromPopup) {
            setSelectedAttorney(null);
            setAttorneyOpenedFromPopup(false);
            setPopupVisible(true);
            const params = new URLSearchParams(location.search);
            params.delete('attorneyId');
            params.delete('fromPopup');
            // Navigate relative to current path might be safer: navigate(`?${params.toString()}`);
            // Or specify the base path if needed: navigate(`${location.pathname}?${params.toString()}`);
            navigate(`?${params.toString()}`);
        } else {
            setPopupVisible(false);
            setPopupData(null);
            setSelectedAttorney(null);
            setSelectedVendor(null);
            setCompanyName('');
            setTelephone('');
            setAddress('');
            setSpecializations([]);

            // Clear query parameters when closing the main popup
            const currentPath = location.pathname; // Keep the current path (/ai, /law-firms, etc.)
            navigate(currentPath); // Navigate to the current path without query params

            // Specific navigation logic - this might need review
            // If you close a popup opened from /law-firms, should it always go back to /law-firms?
            // The code below might conflict with clearing params above.
            // Consider if this specific origin logic is still needed.
            /*
            if (popupOrigin === 'law-firms') {
                navigate('/law-firms'); // This overrides the param clearing above
                setPopupOrigin(null);
            } else if (historyStack.length > 0) {
                 // History stack logic might be complex with query params, review if needed
                navigate(historyStack[historyStack.length - 1]);
                setHistoryStack(prev => prev.slice(0, -1));
            } else {
                 // Navigating to '/' will trigger the redirect. Is this intended when closing a popup?
                 // Maybe navigate to a default path like '/ai' instead?
                navigate('/');
            }
            */
            setPopupOrigin(null); // Reset origin after closing
        }
    };

    // useEffect for URL params - UNCHANGED (but runs on /ai?param=... now too)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const attorneyId = params.get('attorneyId');
        const companyId = params.get('companyId');
        const vendorId = params.get('vendorId');
        const fromPopup = params.get('fromPopup') === 'true';

        // Prevent fetches if redirecting from '/'
        if (location.pathname === '/') return;

        if (attorneyId) {
            api.get(`/attorneys/${attorneyId}/`)
                .then(response => {
                    // Check if component is still mounted and path/search matches? (Advanced)
                    setSelectedAttorney(response.data);
                    setCompanyName(`${response.data.first_name} ${response.data.last_name}`);
                    setTelephone(response.data.phone_number);
                    setAddress(response.data.addresses.map(addr => `${addr.street_address}, ${addr.city.name}, ${addr.state.name}, ${addr.country.name}, ${addr.zip_code.zip_code}`).join('\n'));
                    setSpecializations(response.data.specializations.map(spec => spec.name));
                    setPopupVisible(true);
                    setCurrentURL(window.location.href);
                    setPopupData(null);
                    setSelectedVendor(null);
                    setAttorneyOpenedFromPopup(fromPopup);
                })
                .catch(error => console.error('Error fetching attorney data:', error));
        } else if (!params.has('companyId') && !params.has('vendorId')) { // Clear only if no other relevant param
            setSelectedAttorney(null);
            setAttorneyOpenedFromPopup(false);
        }

        if (companyId) {
            api.get(`/legal-companies/${companyId}/`)
                .then(response => {
                    setPopupData(response.data);
                    setCompanyName(response.data.name);
                    setTelephone(response.data.phone_number);
                    setAddress(`${response.data.address.street_address}, ${response.data.address.city.name}, ${response.data.address.state.name}, ${response.data.address.country.name}, ${response.data.address.zip_code.zip_code}`);
                    setSpecializations(response.data.specializations.map(spec => spec.name));
                    setPopupVisible(true);
                    setCurrentURL(window.location.href);
                    if (!attorneyId && !vendorId) {
                        setSelectedAttorney(null);
                        setSelectedVendor(null);
                    }
                })
                .catch(error => console.error('Error fetching company data:', error));
        } else if (!params.has('attorneyId') && !params.has('vendorId')) { // Clear only if no other relevant param
            setPopupData(null);
        }

        if (vendorId) {
            api.get(`/vendors/${vendorId}/`)
                .then(response => {
                    setSelectedVendor(response.data);
                    setCompanyName(response.data.name);
                    setTelephone(response.data.phone_number);
                    setAddress(`${response.data.address.street_address}, ${response.data.address.city.name}, ${response.data.address.state.name}, ${response.data.address.country.name}, ${response.data.address.zip_code.zip_code}`);
                    setSpecializations(response.data.languages.map(lang => lang.name));
                    setPopupVisible(true);
                    setCurrentURL(window.location.href);
                    if (!attorneyId && !companyId) {
                        setPopupData(null);
                        setSelectedAttorney(null);
                    }
                })
                .catch(error => console.error('Error fetching vendor data:', error));
        } else if (!params.has('attorneyId') && !params.has('companyId')) { // Clear only if no other relevant param
            setSelectedVendor(null);
        }

        // If no ID is present in the URL search params and popup is visible, hide it.
        // This helps close the popup if the user manually removes params from URL or navigates back.
        if (!attorneyId && !companyId && !vendorId && popupVisible) {
            // Check if something IS selected before hiding, to avoid loops maybe?
            if (selectedAttorney || popupData || selectedVendor) {
                handleHidePopup(); // Use the existing handler to ensure state is cleaned up properly
            }
        }

    }, [location.search, location.pathname]); // Added location.pathname dependency


    // Other handlers - UNCHANGED
    const handlerShowSendOkPopup = useCallback(() => {
        setShowFeedbackPopup(false);
        setShowSendOkPopup(true);
    }, []);

    const handlerCloseOk = useCallback(() => {
        setShowSendOkPopup(false);
    }, []);

    const handlerShowFeebackPopup = useCallback((e) => {
        e.preventDefault();
        setShowFeedbackPopup(true);
    }, []);

    const handlerCloseFeedbackPopup = useCallback(() => {
        setShowFeedbackPopup(false);
    }, []);


    const handlerShowShare = useCallback(() => {
        setShowSharePopup(true);
    }, []);

    const handlerCloseShare = useCallback(() => {
        setShowSharePopup(false);
    }, []);

    return (
        <GoogleOAuthProvider clientId={clientId}>
            <SearchProvider>
                <NotificationProvider>
                    <div className={"page" + (isIndexPage ? ' index' : '')}>
                        {/* Helmet and Header remain the same */}
                        <Helmet>
                            <meta charSet="utf-8"/>
                            {/* Title might need adjustment based on the current route or popup */}
                            <title>{popupData ? popupData.name : selectedVendor ? selectedVendor.name : selectedAttorney ? `${selectedAttorney.first_name} ${selectedAttorney.last_name}` : "Law Firm"}</title>
                            <meta property="og:title"
                                  content={popupData ? popupData.name : selectedVendor ? selectedVendor.name : selectedAttorney ? `${selectedAttorney.first_name} ${selectedAttorney.last_name}` : "Law Firm"}/>
                            <meta property="og:description"
                                  content={popupData ? popupData.description : selectedVendor ? selectedVendor.description : selectedAttorney ? selectedAttorney.description : "Description"}/>
                            <meta property="og:image"
                                  content={popupData ? popupData.logo : selectedVendor ? selectedVendor.logo : selectedAttorney ? selectedAttorney.photo : "default.jpg"}/>
                            <meta property="og:url" content={window.location.href}/>
                        </Helmet>
                        <Header showFeedbackPopup={handlerShowFeebackPopup} showSharePopup={handlerShowShare}
                                isIndexPage={isIndexPage} // This now correctly reflects '/ai' if needed
                                isPopupActive={popupVisible || showFeedbackPopup || showSendOkPopup || showSharePopup}
                                region={region}
                        />
                        <header className="header header-inner min">
                            <Notification/>
                        </header>

                        {/* Routes Definition */}
                        <Routes>
                            {/* Route for '/' still exists but redirects */}
                            <Route path="/"
                                   element={<MainContent onShowPopup={handleShowPopup}
                                                         isIndexPage={isIndexPage} region={region}/>}/>

                            {/* *** ADDED THIS NEW ROUTE FOR /ai *** */}
                            <Route path="/ai"
                                   element={<MainContent onShowPopup={handleShowPopup}
                                                         isIndexPage={isIndexPage} region={region}/>}/>
                            {/* ************************************ */}

                            {/* Other existing routes */}
                            <Route path="/bio" element={<PageAbout/>}/>
                            <Route path="/law-firms" element={<PageLawFirms onShowPopup={handleShowPopup}/>}/>
                            <Route path="/vendors" element={<PageVendors onShowPopup={handleShowPopup}/>}/>
                            <Route path="/sign-in" element={<SignIn/>}/>
                            <Route path="/sign-up" element={<SignUp/>}/>
                            <Route path="/profile" element={
                                // <ProtectedRoute>
                                <UserProfile onShowPopup={handleShowPopup}/>
                                // </ProtectedRoute>
                            }/>
                            <Route path="/forgot-password" element={<ForgotPassword/>}/>
                            <Route path="/reset-password/:uid/:token" element={<ResetPassword/>}/>
                            <Route path="/activate/:uidb64/:token/" element={<ActivateAccount/>}/>
                            <Route path="/activate-email-token/:token/" element={<ActivateTokenAccount/>}/>
                            <Route path="/resend-activation-email" element={<ResendActivationEmail/>}/>
                            <Route path="/privacy-policy" element={<PrivacyPolicy/>}/>
                            <Route path="/terms-of-use" element={<TermsOfUse/>}/>
                            <Route path="/cookie-policy" element={<CookiePolicy/>}/>
                            <Route path="/dmca-policy" element={<DMCAPolicy/>}/>
                            <Route path="/attorney-adv-rules" element={<AttorneyAdvRules/>}/>
                            <Route path="/security-policy" element={<SecurityPolicy/>}/>
                            <Route path="/accessibility" element={<Accessibility/>}/>
                            <Route path="/verify-email/:token/" element={<VerifyEmail/>}/>
                            <Route path="/activate-promo-email-token/:token" element={<VerifyPromoEmailToken/>}/>
                            <Route path="/activate-new-promo-email-token/:token" element={<VerifyNewPromoEmailToken/>}/>
                            <Route path="/claim/confirm/:token" element={<DisplayPromoAttorneyInfo/>}/>
                            <Route path="/claim-vendor/:token" element={<ClaimVendorConfirmationPage/>}/>
                            <Route path="/promo-claim/:token" element={<MarketingClaimProfilePage/>}/>
                            <Route path="/promo-claim-vendor/:token" element={<MarketingClaimVendorProfilePage/>}/>
                            <Route path="/searching-request/:resultToken"
                                   element={<SearchingRequestPage onShowPopup={handleShowPopup}/>}/> </Routes>

                        {/* Popups remain the same */}
                        {popupVisible && !!popupData && (
                            <Popup
                                visible={popupVisible}
                                data={popupData}
                                origin={popupOrigin}
                                onClose={handleHidePopup}
                                showSharePopup={() => handlerShowShare({
                                    companyName,
                                    telephone,
                                    address,
                                    specializations
                                })}
                                handleShowPopup={handleShowPopup}
                            />

                        )}
                        {popupVisible && selectedAttorney && (
                            <PopupAttorney
                                visible={popupVisible}
                                attorney={selectedAttorney}
                                onClose={handleHidePopup}
                                origin={popupOrigin}
                                showSharePopup={() => handlerShowShare({
                                    companyName: `${selectedAttorney.first_name} ${selectedAttorney.last_name}`,
                                    telephone: selectedAttorney.phone_number,
                                    address: selectedAttorney.addresses.map(addr => `${addr.street_address}, ${addr.city.name}, ${addr.state.name}, ${addr.country.name}, ${addr.zip_code.zip_code}`).join('\n'),
                                    specializations: selectedAttorney.specializations.map(spec => spec.name)
                                })}
                            />
                        )}
                        {popupVisible && selectedVendor && (
                            <PopupVendor
                                visible={popupVisible}
                                vendor={selectedVendor}
                                onClose={handleHidePopup}
                                origin={popupOrigin}
                                showSharePopup={() => handlerShowShare({
                                    companyName: selectedVendor.name,
                                    telephone: selectedVendor.phone_number,
                                    address: `${selectedVendor.address.street_address}, ${selectedVendor.address.city.name}, ${selectedVendor.address.state.name}, ${selectedVendor.address.country.name}, ${selectedVendor.address.zip_code.zip_code}`,
                                    specializations: selectedVendor.languages.map(lang => lang.name)
                                })}
                            />
                        )}
                        {showFeedbackPopup &&
                            <FeedbackPopup show={showFeedbackPopup} showSendOkPopup={handlerShowSendOkPopup}
                                           closeFeedbackPopup={handlerCloseFeedbackPopup}/>}
                        {showSendOkPopup && <SendOkPopup closePopup={handlerCloseOk}/>}
                        {showSharePopup &&
                            <SharePopup closePopup={handlerCloseShare} shareAddress={currentURL}
                                        copyEmbed='Embed code'
                                        companyName={companyName} telephone={telephone} address={address}
                                        specializations={specializations}/>}

                    </div>
                </NotificationProvider>
            </SearchProvider>
        </GoogleOAuthProvider>
    );
};

export default App;